import React, {useState} from 'react'
import {Formik, Form} from 'formik'
import * as Yup from 'yup'
import moment from 'moment'

interface Props {
  show: boolean
  handleClose: () => void
  handleOk: () => void
  workOrder: any
  defaultDueDate: string
  onUpdateDueDate: (newDueDate: string, reason: string) => Promise<void>
}

const DueDateModal: React.FC<Props> = ({
  show,
  handleClose,
  handleOk,
  workOrder,
  defaultDueDate,
  onUpdateDueDate,
}) => {
  const [dueDateLoading, setDueDateLoading] = useState(false)

  const dueDateValidationSchema = Yup.object().shape({
    dueDate: Yup.string().required('New Due Date is required'),
    dueDateReason: Yup.string()
      .required('Reason is required')
      .test('trim', 'Reason is required', (value) => value?.trim().length > 0),
  })

  const dueDateInitialValues = {
    dueDate: defaultDueDate,
    dueDateReason: '',
  }

  return (
    <Formik
      initialValues={dueDateInitialValues}
      validationSchema={dueDateValidationSchema}
      onSubmit={async (values, {setSubmitting, resetForm}) => {
        setDueDateLoading(true)
        await onUpdateDueDate(values.dueDate, values.dueDateReason)

        setDueDateLoading(false)
        document.getElementById('rd_dueDate_modal_dismiss')!.click()
        handleOk()
        resetForm()
        setSubmitting(false)
      }}
      enableReinitialize
    >
      {({errors, touched, isSubmitting, values, setFieldValue, resetForm}: any) => (
        <>
          <div
            className={`modal fade ${show ? 'show' : ''}`}
            tabIndex={-1}
            id='dueDate_modal'
            style={{display: show ? 'block' : 'none'}}
          >
            <Form>
              <div className='modal-dialog modal-dialog-centered'>
                <div className='modal-content'>
                  <div className='modal-header'>
                    <h5 className='modal-title'>Set Due Date</h5>

                    <div
                      className='btn btn-icon btn-sm btn-active-light-primary ms-2'
                      data-bs-dismiss='modal'
                      aria-label='Close'
                      onClick={() => {
                        handleClose()
                        resetForm()
                      }}
                    >
                      <span className='svg-icon svg-icon-2x'></span>
                    </div>
                  </div>

                  <div className='modal-body'>
                    <div className='mb-3'>
                      <label htmlFor='dueDateFrom' className='form-label'>
                        Current Due Date
                      </label>
                      <input
                        type='datetime-local'
                        name='dueDateFrom'
                        className='form-control form-control-solid'
                        id='dueDateFrom'
                        value={
                          workOrder.dueDate ? moment(workOrder.dueDate).format('YYYY-MM-DDTHH:mm') : ''
                        }
                        disabled
                      />
                    </div>
                    <div className='mb-3'>
                      <label htmlFor='dueDate' className='required form-label'>
                        New Due Date
                      </label>
                      <input
                        type='datetime-local'
                        name='dueDate'
                        placeholder=''
                        className={`form-control form-control-solid ${
                          touched.dueDate && errors.dueDate ? 'is-invalid' : ''
                        }`}
                        id='dueDate'
                        value={values.dueDate}
                        onChange={(e) => {
                          setFieldValue('dueDate', e.target.value)
                        }}
                      />
                      {touched.dueDate && errors.dueDate && (
                        <div className='fv-plugins-message-container'>
                          <span className='text-danger' role='alert'>
                            {errors.dueDate}
                          </span>
                        </div>
                      )}
                    </div>
                    <div className='mb-3'>
                      <label htmlFor='dueDateReason' className='required form-label'>
                        Reason
                      </label>
                      <textarea
                        name='dueDateReason'
                        className={`form-control form-control-solid ${
                          touched.dueDateReason && errors.dueDateReason ? 'is-invalid' : ''
                        }`}
                        id='dueDateReason'
                        rows={3}
                        value={values.dueDateReason}
                        onChange={(e) => {
                          setFieldValue('dueDateReason', e.target.value)
                        }}
                        placeholder='Enter reason for due date change...'
                      />
                      {touched.dueDateReason && errors.dueDateReason && (
                        <div className='fv-plugins-message-container'>
                          <span className='text-danger' role='alert'>
                            {errors.dueDateReason}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className='modal-footer'>
                    <button
                      type='button'
                      onClick={() => {
                        resetForm()
                        handleClose()
                      }}
                      className='btn btn-light'
                      data-bs-dismiss='modal'
                      id='rd_dueDate_modal_dismiss'
                    >
                      Close
                    </button>
                    <button
                      type='submit'
                      id='rd_set_dueDate_submit'
                      className='btn btn-lg btn-primary'
                      disabled={isSubmitting || dueDateLoading}
                    >
                      {!isSubmitting && !dueDateLoading && (
                        <span className='indicator-label'>Confirm</span>
                      )}
                      {(isSubmitting || dueDateLoading) && (
                        <span className='indicator-progress' style={{display: 'block'}}>
                          Please wait...
                          <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                        </span>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </Form>
          </div>
          {show && <div className='modal-backdrop fade show'></div>}
        </>
      )}
    </Formik>
  )
}

export default DueDateModal

